import {
  Component,
  OnInit,
  Input,
  ViewChild,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { BreadcrumbService, MetricsGridConfig } from '@/app/core';
import { FormatService } from '@/app/core/query/format.service';
import { CoreModule } from '@/app/core/core.module';
import { TemplatePortal } from '@angular/cdk/portal';

@Component({
  selector: 'rdo-saved-time',
  styles: `
    .saved-time {
      display: flex;
      height: 100%;
      width: auto;
      align-items: center;
      justify-content: flex-end;
      gap: 10px;
    }

    .saved-time__label {
      font-weight: 700;
    }

    .rdo-selector__time {
      font-weight: normal;
      margin-right: 5px;
    }
  `,
  template: `
    <ng-template #savedTimeTemplate>
      @if (getLastUpdatedLabel(); as label) {
        <div class="saved-time">
          <span class="saved-time__label">{{ label }}</span>
          <span class="rdo-selector__time">{{ getLastUpdatedDateTime() }}</span>
        </div>
      }
    </ng-template>
  `,
  imports: [TranslatePipe, CoreModule],
})
export class LastSavedTimeComponent implements OnInit {
  @ViewChild('savedTimeTemplate') viewTemplate: TemplateRef<any>;
  @Input() gridConfig: MetricsGridConfig;
  private portal: TemplatePortal;
  private portalId = 'savedTimePortal';

  constructor(
    protected translateService: TranslateService,
    private formatService: FormatService,
    private breadcrumbService: BreadcrumbService,
    private viewContainerRef: ViewContainerRef
  ) {}

  ngOnInit(): void {
    setTimeout(() => {
      if (this.viewTemplate) {
        this.portal = new TemplatePortal(
          this.viewTemplate,
          this.viewContainerRef
        );
        // Register with unique ID
        this.breadcrumbService.registerPortal(this.portalId, this.portal);
      }
    });
  }

  ngOnDestroy(): void {
    this.breadcrumbService.unregisterPortal(this.portalId);
  }

  getLastUpdatedLabel(): string {
    if (!this.gridConfig?.lastUpdated) {
      return '';
    }
    return this.formatService.translateAndFormat(
      'main.core.tables.common.last_updated'
    );
  }

  getLastUpdatedDateTime(): string {
    if (!this.gridConfig?.lastUpdated) {
      return '';
    }

    // Use getDateLocale() (not localeService) so the de-DE→en-GB override is applied
    // when determining date order, matching what rdoLocalizedDate pipe does.
    const dateLocale = this.formatService.getDateLocale();

    // gridConfig.lastUpdated is typed as Date but may arrive as a string at runtime.
    const raw = this.gridConfig.lastUpdated;
    const date =
      typeof raw === 'string'
        ? DateTime.fromISO(raw, { zone: 'America/Los_Angeles' })
        : DateTime.fromJSDate(raw, { zone: 'America/Los_Angeles' });

    if (!date.isValid) {
      return '';
    }

    // All three parts derive from the same Luxon DateTime so they always
    // agree on the calendar day, even for timestamps near midnight Pacific.
    const localizedDate = date.setLocale(dateLocale);
    const dateShort = localizedDate.toLocaleString(DateTime.DATE_SHORT);
    const timeStr = localizedDate.toFormat('t');
    const zone = localizedDate.toFormat('ZZZZ');

    return `${dateShort} ${timeStr} (${zone})`;
  }
}
